import { useState } from 'react';
import { PROVIDERS, type ProxyLab } from '../../hooks/useProxyAdmin';
import {
  CREDENTIAL_KINDS,
  isKnownCredentialKind,
  optionsWith,
  LAB_APIS,
  requiresBaseUrl,
  requiresCredential,
  usesBaseUrl,
  usesCredential,
  usesUnsupportedPaths,
} from '../../utils/middlemanRules';
import {
  CheckField,
  Section,
  SelectField,
  TextAreaField,
  TextField,
} from './FormFields';
import { FormDialog } from './FormDialog';
import { buildLabBody, draftFromLab, type LabDraft } from './labDraft';

/** Add a lab, or change one; only the fields its API uses are shown. */
export function LabForm({
  lab,
  busy,
  onSubmit,
  onCancel,
}: {
  /** Null to add a lab. */
  lab: ProxyLab | null;
  busy: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(() => draftFromLab(lab));
  const [initial] = useState(() => JSON.stringify(draft));
  const [problem, setProblem] = useState<string | null>(null);
  const set = <K extends keyof LabDraft>(key: K, value: LabDraft[K]) =>
    setDraft(d => ({ ...d, [key]: value }));
  const creating = lab === null;

  return (
    <FormDialog
      label={creating ? 'Add lab' : `Edit lab ${lab.name}`}
      title={
        creating ? (
          'Add lab'
        ) : (
          <>
            Edit lab <span className="font-mono">{lab.name}</span>
          </>
        )
      }
      dirty={JSON.stringify(draft) !== initial}
      busy={busy}
      problem={problem}
      onClose={onCancel}
      onSubmit={() => {
        const built = buildLabBody(draft, lab);
        if ('error' in built) {
          setProblem(built.error);
          return;
        }
        setProblem(null);
        onSubmit(built.body);
      }}
    >
      <Section legend="Route">
        <TextField
          label="Name"
          required
          disabled={!creating}
          value={draft.name}
          onChange={v => set('name', v)}
          hint={
            creating
              ? 'Model configs name their lab by this.'
              : "Model configs reference the lab by name, so it can't change."
          }
        />
        <SelectField
          label="API"
          required
          value={draft.api}
          options={optionsWith<string>(LAB_APIS, draft.api)}
          onChange={v => set('api', v)}
        />
        {usesBaseUrl(draft.api) && (
          <TextField
            label="Base URL"
            required={requiresBaseUrl(draft.api)}
            className="w-96"
            value={draft.base_url}
            placeholder="https://api.example.com/v1"
            onChange={v => set('base_url', v)}
            hint="Request paths are appended to it. {gcp_project} may appear in the path."
          />
        )}
        <SelectField
          label="Provider"
          value={draft.provider}
          options={[
            { value: '', label: 'None' },
            ...optionsWith<string>(PROVIDERS, draft.provider),
          ]}
          onChange={v => set('provider', v)}
          hint="The provider a model on this lab gets when it records none."
        />
        <CheckField
          label="Chat API"
          checked={draft.is_chat}
          onChange={v => set('is_chat', v)}
        />
      </Section>
      {usesCredential(draft.api) && (
        <Section legend="Credential">
          <SelectField
            label="Credential kind"
            required={requiresCredential(draft.api)}
            value={draft.credential_kind}
            options={[
              { value: '', label: 'Choose…', disabled: true },
              ...optionsWith<string>(CREDENTIAL_KINDS, draft.credential_kind),
            ]}
            onChange={v => set('credential_kind', v)}
          />
          {(draft.credential_kind === 'api_key' ||
            (draft.credential_kind !== '' &&
              !isKnownCredentialKind(draft.credential_kind))) && (
            <>
              <TextField
                label="API key name"
                required
                value={draft.api_key_name}
                placeholder="EXAMPLE_API_KEY"
                onChange={v => set('api_key_name', v)}
                hint="A provider-key store entry; the deployment's key-host allowlist must allow it for this host."
              />
              <TextField
                label="High-priority key name"
                value={draft.high_priority_api_key_name}
                onChange={v => set('high_priority_api_key_name', v)}
              />
            </>
          )}
        </Section>
      )}
      {usesUnsupportedPaths(draft.api) && (
        <div className="mt-3">
          <TextAreaField
            label="Unsupported paths"
            mono
            value={draft.unsupported_paths}
            onChange={v => set('unsupported_paths', v)}
            hint="One per line, relative to the base URL: paths the upstream doesn't implement."
          />
        </div>
      )}
    </FormDialog>
  );
}
